<template>
  <div class="container-fluid bg-light h-100-vh">
    <!-- Search and category -->
    <div class="row">
      <div class="col-md-7 col-12">
        <div class="container pt-3">
          <div class="row py-3">
            <div class="col-md-6 col-sm-12">
              <form @submit.prevent="submitSearchItem" class="form-inline">
                <div class="input-group w-100 my-1">
                  <input
                    type="text"
                    ref="search_item_focus"
                    class="form-control"
                    v-model="searchItemTerm"
                    placeholder="Search Items"
                    autocomplete="off"
                  />
                  <div class="input-group-append">
                    <button class="btn btn-outline-secondary" type="submit">
                      <i class="fa fa-search"></i>
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <div class="col-md-6 col-sm-12">
              <b-form-select v-model="selectedCategory" :options="prodCategoryOptions" class="my-1">
                <template #first>
                  <b-form-select-option :value="''">-- All Categories --</b-form-select-option>
                </template>
              </b-form-select>
            </div>
          </div>
        </div>

        <div class="container">
          <div class="row">
            <div class="col-md-4 col-sm-12">
              <h5 class="py-3">
                {{ s_productcategory }}
                <span v-if="isServerSearch"> — {{ serverSearchTotal }} results</span>
                <span v-else-if="isPartialCatalog"> — {{ filteredProducts.length }} shown of {{ totalCatalogSize }}</span>
                <span v-else> — {{ filteredProducts.length }}</span>
                <span v-if="serverSearchLoading" class="ml-2 text-muted" style="font-size:12px">searching…</span>
              </h5>
            </div>

            <div class="col-md-4 col-sm-6">
              <div class="py-3" v-if="companySettings.has_wholesale === true">
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="customSwitch1"
                    v-model="is_Retail"
                  />
                  <label v-if="is_Retail === true" class="custom-control-label" for="customSwitch1">Retails Prices Set</label>
                  <label v-else class="custom-control-label text-danger" for="customSwitch1">Wholesale Prices Set</label>
                </div>
              </div>
            </div>

            <div class="col-md-3 col-sm-5">
              <select v-model="sortProducts" class="form-control">
                <option value="null">Sort</option>
                <option value="name-desc">Name Desc</option>
                <option value="name-asc">Name Asc</option>
                <option value="price-asc">Price Asc</option>
                <option value="price-desc">Price Desc</option>
              </select>
            </div>

            <div class="col-md-1 col-sm-1 text-center">
              <a href="#" class="text-dark" @click.prevent="hotReload(false, true)"><span class="align-middle"><i class="fa fa-refresh"></i></span></a>
              <p><small>Reload</small></p>
            </div>
          </div>
        </div>

        <!-- view toggle + product listing -->
        <div v-if="displayedProducts.length > 0 || serverSearchLoading" style="margin-bottom: 5em;">
          <div class="d-flex justify-content-end mb-3 pt-1">
            <label class="switch">
              <input type="checkbox" v-model="isListView" />
              <span class="slider round"></span>
            </label>
            <span class="ml-2">{{ isListView ? 'List View' : 'Grid View' }}</span>
          </div>

          <!-- List View -->
          <div v-if="isListView">
            <div class="list-group">
              <div
                v-for="product in displayedProducts"
                :key="product.id"
                class="list-group-item d-flex align-items-center p-3 shadow-sm"
                :class="{ 'out-of-stock-dim': isOutOfStock(product) }"
                @click="handleProductClick(product)"
              >
                <!-- Product Image -->
                <img
                  v-if="product.img"
                  :src="product.img"
                  class="rounded mr-3 image-table"
                  alt=""
                />
                <div v-else class="rounded mr-3 d-flex align-items-center justify-content-center" :style="{ width: '60px', height: '60px', background: product.bg_color }">
                  <span class="text-light">{{ product.product.charAt(0) }}</span>
                </div>

                <!-- Product Details -->
                <div class="flex-grow-1">
                  <h6 class="mb-0">{{ product.product }}</h6>
                  <!-- <small v-if="canSeeStock" class="badge badge-secondary">{{ product.remaining_units }} in stock</small> -->
                  <!-- <span v-if="product.is_service" class="badge badge-info ml-1">SERVICE</span> -->
                  <span v-if="isOutOfStock(product)" class="badge badge-warning ml-1">Out of stock — Borrow?</span>
                  <span v-else-if="canBorrowForProduct(product)" class="badge badge-light ml-1" title="Selling from the stock buffer">0 in stock</span>
                  <span v-if="product.campaign_label" class="badge badge-primary ml-1">{{ product.campaign_label }}</span>
                </div>

                <!-- Add to Cart / Borrow -->
                <button v-if="isOutOfStock(product)" class="btn btn-outline-warning btn-sm" @click.stop="openBorrowModal(product)">
                  Borrow Stock
                </button>
                <template v-else>
                  <button class="btn btn-primary btn-sm" @click.stop="addToCart(product)">
                    Add to Cart
                  </button>
                  <button v-if="canBorrowForProduct(product)" class="btn btn-link btn-sm" @click.stop="openBorrowModal(product)">
                    Borrow instead
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- Grid View -->
          <div v-else>
            <div class="row no-gutters">
              <div
                class="col-md-3 col-6 py-1 px-lg-1"
                v-for="product in displayedProducts"
                :key="product.id"
                :class="{ 'out-of-stock-dim': isOutOfStock(product) }"
                @click="handleProductClick(product)"
              >
                <div v-if="product.img && companySettings.items_have_images" class="card h-100 rounded shadow-sm" style="position:relative;">
                  <span v-if="product.is_service" class="badge badge-info" style="position:absolute;top:6px;right:6px;font-size:9px;z-index:1;">SERVICE</span>
                  <span v-if="isOutOfStock(product)" class="badge badge-warning" style="position:absolute;top:6px;left:6px;font-size:9px;z-index:1;">BORROW?</span>
                  <span v-else-if="canBorrowForProduct(product)" class="badge badge-light" style="position:absolute;top:6px;left:6px;font-size:9px;z-index:1;" title="Selling from the stock buffer - tap to borrow instead" @click.stop="openBorrowModal(product)">BORROW INSTEAD?</span>
                  <div class="card-body d-flex justify-content-center align-items-center">
                    <img :src="product.img" alt="" class="img-fluid card-img-top" />
                  </div>
                  <div class="text-center">
                    <h6>{{ product.product }}</h6>
                  </div>
                </div>

                <div v-else class="card h-100 rounded shadow-sm" style="position:relative;">
                  <span v-if="product.is_service" class="badge badge-info" style="position:absolute;top:6px;right:6px;font-size:9px;z-index:1;">SERVICE</span>
                  <span v-if="isOutOfStock(product)" class="badge badge-warning" style="position:absolute;top:6px;left:6px;font-size:9px;z-index:1;">BORROW?</span>
                  <span v-else-if="canBorrowForProduct(product)" class="badge badge-light" style="position:absolute;top:6px;left:6px;font-size:9px;z-index:1;" title="Selling from the stock buffer - tap to borrow instead" @click.stop="openBorrowModal(product)">BORROW INSTEAD?</span>
                  <div class="card-body rounded" :style="{ background: product.bg_color }">
                    <div class="square-item-bg">
                      <div class="text-center py-5">
                        <h6 class="text-center text-light">{{ product.product }}</h6>
                        <small v-if="canSeeStock" class="badge badge-light rounded px-1 py-1 mt-2">{{ product.remaining_units }}</small>
                        <span v-if="product.campaign_label" class="badge badge-primary d-block mt-1">{{ product.campaign_label }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div> <!-- /col -->
            </div> <!-- /row -->
          </div>

          <!-- Pagination: server search results -->
          <div v-if="isServerSearch && serverSearchHasMore" class="text-center py-3">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="serverSearchLoading"
              @click="loadMoreServerResults"
            >
              {{ serverSearchLoading ? 'Loading…' : 'Load more results' }}
            </button>
          </div>

          <!-- Pagination: hot catalog virtual scroll -->
          <div v-if="!isServerSearch && filteredProducts.length > displayLimit" class="text-center py-3">
            <button class="btn btn-outline-secondary btn-sm" @click="displayLimit += 100">
              Show more ({{ filteredProducts.length - displayLimit }} remaining)
            </button>
          </div>
        </div>
        <!-- end product listing -->
      </div> <!-- /left column -->

      <!-- RIGHT COLUMN (Cart) -->
      <div class="col-md-5 d-none d-sm-block col-12">
        <div class="card h-100 d-none d-sm-block">
          <div class="card-body">
            <div class="text-dark text-center">Cart ({{ $store.state.cartCount }})</div>
            <hr />
            <div class="text-center" v-if="message_feedback">
              <h6 class="text-danger">{{ message_feedback }}</h6>
            </div>

            <button class="btn btn-block btn-outline-dark" @click="showCustomersModal">
              <span class="h5"><i class="bi bi-person-circle"></i></span>
              <span v-if="$store.state.customerdata !== 'Select Customer'">{{$store.state.customerdata.full_name}}</span>
              <span v-else class="text-danger">Add Customer Details</span>
            </button>

            <div v-if="$store.state.cart.length > 0" class="navbar-dropdown is-boxed is-right">
              <div class="table-responsive">
                <table class="table">
                  <tbody>
                    <tr v-for="cart in $store.state.cart" :key="cart.id">
                      <td>
                        <span class="h6">{{ cart.product }}</span>
                        <span v-if="cart.is_borrowed" class="badge badge-warning" title="Fulfilled from borrowed stock">Borrowed</span>
                        <select
                          v-if="cart.units && cart.units.length > 1 && !cart.is_borrowed"
                          class="form-control form-control-sm d-inline-block ml-1"
                          style="width:auto;max-width:110px;"
                          :value="cart.unit_id || (cart.units.find(u => u.is_base_unit) || cart.units[0]).id"
                          @change="changeCartUnit(cart, $event.target.value)"
                        >
                          <option v-for="unit in cart.units" :key="unit.id" :value="unit.id">{{ unit.unit_name }}</option>
                        </select>
                      </td>
                      <td><span :class="['badge', cart.is_borrowed ? 'badge-warning' : 'badge-secondary']" @click="changeQuantity(cart)">{{ cart.quantity }}</span></td>
                      <td @click="cart.is_service || companySettings.allows_price_change_on_the_go ? changePricingOnTheGo(cart) : null" class="border-0">
                        <span class="h6">{{ convertCurrencyToLocal(cart.totalPrice) }}</span>
                        <i v-if="showRoundTotalTrigger(cart)" class="bi bi-arrow-repeat text-muted ml-1" title="Round total for this quantity" style="font-size:.8rem; cursor:pointer;" @click.stop="openRoundTotalModal(cart)"></i>
                      </td>
                      <td class="border-0 align-middle">
                        <span class="removeBtn" title="Remove from cart" @click.prevent="removeFromCart(cart)"><i class="fa fa-trash"></i></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <hr />
            <div class="row">
              <div class="float-left col-6"><h6 class="text-left">Sub Total</h6></div>
              <div class="float-right col-6"><h6 class="text-right">{{ convertCurrencyToLocal(totalPrice) }}</h6></div>
            </div>

            <template v-if="companySettings.enable_order_discount !== false">
              <hr />
              <div class="row">
                <div class="col-6"><h6 class="text-left">Order discount</h6></div>
                <div class="col-6">
                  <div class="input-group mb-2">
                    <div class="input-group-prepend">
                      <div class="input-group-text">{{ userDetails.company?.companysettings?.currency }}</div>
                    </div>
                    <input type="number" v-model.number="totalDiscountApplied" class="form-control" id="inlineFormInputGroup" />
                  </div>
                  <div v-if="discountError"><small class="text-danger">{{ discountError }}</small></div>
                </div>
              </div>
            </template>

            <hr />
            <div class="row">
              <div class="float-left col-6"><h6 class="text-left">Grand Total</h6></div>
              <div class="float-right col-6"><h6 class="text-right">{{ convertCurrencyToLocal(grandTotalAmount) }}</h6></div>
            </div>

            <hr />
            <div class="pt-4">
              <button v-if="!companySettings.skip_checkout_done" ref="submitButton" @click="Submit" :class="['btn', 'btn-primary', 'btn-block', { disabled: isSubmitting }]" :disabled="isSubmitting">Complete - {{ convertCurrencyToLocal(grandTotalAmount) }}</button>
              <div v-else><MiniCheckout /></div>
            </div>
          </div>
        </div>
      </div> <!-- /right column -->
    </div> <!-- /row -->

    <!-- mobile FAB -->
    <a href="#" @click.prevent="showCartModal" class="float d-sm-block d-xs-block d-md-none text-light">
      <i class="fa fa-shopping-bag my-float"></i>
      <span class="text-light cart-count-badge" :class="{ 'cart-count-pulse': cartBadgePulse }">{{ $store.state.cartCount }}</span>
    </a>

    <!-- cart modal (desktop/mobile reuse) -->
    <b-modal ref="cart-modal" hide-footer>
      <div class="card h-100">
        <div class="card-body">
          <div class="text-dark text-center">Cart ({{ $store.state.cartCount }})</div>
          <hr />
          <div class="text-center" v-if="message_feedback">
            <h6 class="text-danger">{{ message_feedback }}</h6>
          </div>

          <button class="btn btn-block btn-outline-dark" @click="showCustomersModal">
            <span class="h5"><i class="bi bi-person-circle"></i></span>
            <span v-if="$store.state.customerdata !== 'Select Customer'">{{$store.state.customerdata.full_name}}</span>
            <span v-else class="text-danger">Add Customer Details</span>
          </button>

          <div v-if="$store.state.cart.length > 0" class="navbar-dropdown is-boxed is-right">
            <div class="table-responsive">
              <table class="table">
                <tbody>
                  <tr v-for="cart in $store.state.cart" :key="cart.id">
                    <td><small>{{ cart.product }}</small> <small v-if="cart.is_borrowed" class="badge badge-warning" title="Fulfilled from borrowed stock">Borrowed</small></td>
                    <td><small :class="['badge', cart.is_borrowed ? 'badge-warning' : 'badge-secondary']" @click="changeQuantity(cart)">{{ cart.quantity }}</small></td>
                    <td @click="cart.is_service || companySettings.allows_price_change_on_the_go ? changePricingOnTheGo(cart) : null" class="border-0">
                      <small>{{ convertCurrencyToLocal(cart.totalPrice) }}</small>
                      <i v-if="showRoundTotalTrigger(cart)" class="bi bi-arrow-repeat text-muted ml-1" title="Round total for this quantity" style="font-size:.8rem; cursor:pointer;" @click.stop="openRoundTotalModal(cart)"></i>
                    </td>
                    <td class="border-0 align-middle">
                      <span class="removeBtn" title="Remove from cart" @click.prevent="removeFromCart(cart)"><i class="fa fa-trash"></i></span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr />
          <div class="row">
            <div class="float-left col-6"><h6 class="text-left">Sub Total </h6></div>
            <div class="float-right col-6"><h6 class="text-right">{{ convertCurrencyToLocal(totalPrice) }}</h6></div>
          </div>

          <template v-if="companySettings.enable_order_discount !== false">
            <hr />
            <div class="row">
              <div class="col-6"><h6 class="text-left">Order discount</h6></div>
              <div class="col-6">
                <div class="input-group mb-2">
                  <div class="input-group-prepend">
                    <div class="input-group-text">{{ userDetails.company?.companysettings?.currency }}</div>
                  </div>
                  <input type="number" v-model.number="totalDiscountApplied" class="form-control" id="inlineFormInputGroup" />
                </div>
                <div v-if="discountError"><small class="text-danger">{{ discountError }}</small></div>
              </div>
            </div>
          </template>

          <hr />
          <div class="row">
            <div class="float-left col-6"><h6 class="text-left">Grand Total </h6></div>
            <div class="float-right col-6"><h6 class="text-right">{{ convertCurrencyToLocal(grandTotalAmount) }}</h6></div>
          </div>

          <hr />
          <div class="pt-4">
            <button v-if="!companySettings.skip_checkout_done" ref="submitButton" @click="Submit" :class="['btn', 'btn-primary', 'btn-block', { disabled: isSubmitting }]" :disabled="isSubmitting">Complete - {{ convertCurrencyToLocal(grandTotalAmount) }}</button>
            <div v-else><MiniCheckout /></div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- change price modal -->
    <b-modal ref="changePriceModal" hide-footer static>
      <div class="d-block text-center">
        <h2 class="font-weight-bold">Price</h2>
        <form @submit.prevent="processNewPricesOnTheGo">
          <input class="form-control border border-dark my-3" v-model="newServicePricing" type="text" />
          <small class="text-danger">Note this is the selling price per item/service. Current - {{ selecteCartDetails.ppu }}</small>
          <input class="form-control border border-dark my-3" :value="selecteCartDetails.id" type="hidden" />
          <button type="submit" class="btn btn-block btn-primary">Update Amount</button>
        </form>
      </div>
    </b-modal>

    <!--
      Round total modal - a deliberately separate flow from "change price
      modal" above. That one edits the PER-UNIT price (common case, any
      qty) and must keep meaning "per unit" or a cashier correcting e.g. a
      2-qty line's rate gets silently reinterpreted as a line total. This
      one only ever exists for a fractional-qty line (see
      showRoundTotalTrigger) and only ever edits this line's TOTAL,
      leaving ppu untouched so a later quantity change still recomputes
      from the real catalog rate, not a corrupted one.
    -->
    <b-modal ref="roundTotalModal" hide-footer static title="Round total">
      <div class="d-block text-center" v-if="selectedRoundTotalCart">
        <h2 class="font-weight-bold">Round total</h2>
        <p class="small text-muted mb-2">
          {{ selectedRoundTotalCart.product }} &middot; {{ selectedRoundTotalCart.quantity }} units
        </p>
        <form @submit.prevent="saveRoundedTotal">
          <input class="form-control border border-dark my-3" v-model="newLineTotal" type="number" step="0.01" min="0" />
          <small class="text-danger">
            This only rounds the total charged for this quantity - the per-unit price stays {{ convertCurrencyToLocal(selectedRoundTotalCart.ppu) }}/unit, and can't go below {{ convertCurrencyToLocal(roundTotalFloor) }} for this quantity.
          </small>
          <button type="submit" class="btn btn-block btn-primary">Update Total</button>
        </form>
      </div>
    </b-modal>

    <!-- quantity modal -->
    <b-modal ref="orderqty-modal" hide-footer static>
      <div class="d-block text-center">
        <h2 class="font-weight-bold">Quantity</h2>
        <form @submit.prevent="processNewQty">
        <input
        type="number"
        step="any"
        min="0"
        v-model="newQty"
        class="form-control"
    />
          <input class="form-control border border-dark my-3" :value="selecteCartDetails.id" type="hidden" />
          <button type="submit" class="btn btn-block btn-primary">Update Quantity</button>
        </form>
      </div>
    </b-modal>

    <!-- customers modals (reused markup preserved) -->
    <b-modal id="customer-modal-sales" ref="customers-modal" hide-footer static>
      <div class="row">
        <div class="col"><h2 class="font-weight-bold ">{{ selectedCustomer }}</h2></div>
        <div class="col d-flex justify-content-end">
          <a class="btn btn-primary btn-sm" @click="AddnewcustomerModal">New Customer <i class="bi bi-plus-square-fill "></i></a>
        </div>

        <div class="container py-3">
          <div class="form-group has-search">
            <span class="fa fa-search form-control-feedback"></span>
            <input type="text" v-model="searchCustomer" class="form-control border" placeholder="Search" autocomplete="off" />
          </div>

          <ul>
            <li class="border-bottom" v-for="customer in allCustomersFiltered" :key="customer.id" @click="SelectCustomer(customer)">
              <div class="row">
                <div class="col-6">
                  <h6 v-if="customer.full_name" class="text-left">{{ customer.full_name }}</h6>
                  <p class="text-gray d-inline">{{ customer.address_address }}</p>
                </div>
                <div class="col-6"><p class="text-gray text-right">{{ customer.phone }}</p></div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </b-modal>

    <b-modal ref="customers-modal-final" scrollable hide-footer static>
      <div class="container d-flex justify-content-end"><a class="btn btn-primary btn-sm" @click="AddnewcustomerModal">Add New Customer <i class="bi bi-plus-square-fill "></i></a></div>
      <div class="container py-3">
        <div class="form-group has-search">
          <span class="fa fa-search form-control-feedback"></span>
          <input type="text" v-model="searchCustomer" class="form-control border" placeholder="Search" autocomplete="off" />
        </div>

        <ul class="overflow-auto">
          <li class="border-bottom underline-primary" v-for="customer in allCustomersFiltered" :key="customer.id" @click="SelectCustomerFinal(customer)">
            <div class="row">
              <div class="col-6">
                <h6 v-if="customer.full_name" class="text-left">{{ customer.full_name }}</h6>
                <p class="text-gray d-inline">{{ customer.address_address }}</p>
              </div>
              <div class="col-6"><p class="text-gray text-right">{{ customer.phone }}</p></div>
            </div>
          </li>
        </ul>
      </div>
    </b-modal>

    <b-modal ref="default-customers" hide-footer static>
      <div class="text-center">
        <h5 class="text-dark">Customer data</h5>
        <hr />
        <img src="/images/customer.png" style="max-height: 200px; width:auto" />
        <div class="btn-group">
          <a class="btn btn-outline-secondary btn-sm mx-1 rounded text-truncate" @click="continueWithoutCustomer()">Continue without customer</a>
          <a @click="showCustomersModalFinal" class="btn btn-outline-primary btn-sm mx-1 rounded">Select customer</a>
        </div>
      </div>
    </b-modal>

    <b-modal ref="new-customers-modal" hide-footer static>
      <div class="row">
        <div class="col"><a @click="showCustomersModal"><h6 class="text-primary"><i class="fas fa-angle-left"></i> Back</h6></a></div>
        <div class="col text-centre"><h2 class="font-weight-bold text-dark ">Add Customer</h2></div>
      </div>

      <div class="text-left" v-html="successMessage">{{ successMessage }}</div>
      <div class="text-left text-danger py-2" v-if="customerForm.errorMessage !== ''">{{ customerForm.errorMessage }}</div>

      <div class="container py-3">
        <form @submit.prevent="newCustomerInfo">
          <div class="form-row py-1">
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.f_name" name="f_name" type="text" placeholder="First Name" /></div>
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.l_name" name="l_name" type="text" placeholder="Last Name" /></div>
          </div>

          <div class="form-group py-1"><input class="form-control border-bottom" name="c_email" v-model="customerForm.c_email" type="email" placeholder="Email" /></div>
          <div class="form-group"><input class="form-control border-bottom" v-model="customerForm.phone_no" name="phone_no" type="text" placeholder="Phone Number" /></div>

                 <div v-if="companySettings.enable_tims">
        <div  class="form-group py-1" >

            <input class="form-control border-bottom" name="krapin" v-model="customerForm.krapin" type="text" placeholder="KRA PIN"/>
        </div>
        <div  class="form-group py-1" >

            <input class="form-control border-bottom" name="company" v-model="customerForm.company" type="text" placeholder="Name of Company"/>
        </div>
    </div>
          <div class="form-row py-1">
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.h_no" name="h_no" type="text" placeholder="House Number" /></div>
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.apartment" name="apartment" type="text" placeholder="Apartment" /></div>
          </div>

          <div class="form-row py-1">
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.street" name="street" type="text" placeholder="Street" /></div>
            <div class="col-6"><input class="form-control border-bottom" v-model="customerForm.town" name="town" type="text" placeholder="Town" /></div>
          </div>

          <div class="form-group pt-3"><button class="btn btn-primary btn-block">Add Customer</button></div>
        </form>
      </div>
    </b-modal>

    <borrow-stock-modal ref="borrowStockModal" @borrowed="onBorrowed"></borrow-stock-modal>
    <variant-picker-modal ref="variantPickerModal" @selected="onVariantSelected"></variant-picker-modal>
  </div>
</template>

<script>
import { createProductUpdateBatcher } from '../services/productUpdateBatcher';
import { mapGetters } from 'vuex';
import MiniCheckout from './partials/MiniCheckout.vue';
import BorrowStockModal from './Sales/BorrowStockModal.vue';
import VariantPickerModal from './Sales/VariantPickerModal.vue';
import productCacheService from './IndexDB/productCacheService';
import customerCacheService from './IndexDB/customerCacheService';
import { getEcho } from '../services/echo';

export default {
  name: 'SalesPage',
  components: { MiniCheckout, BorrowStockModal, VariantPickerModal },

  mounted() {
    this.displayData('').then(() => {
      this.$nextTick(() => {
        if (this.$refs.search_item_focus && this.$refs.search_item_focus.focus) {
          this.$refs.search_item_focus.focus();
        }
      });
      // Paint instantly from whatever's cached, then silently confirm we're
      // current: a plain page nav shouldn't depend on the 15-min cache TTL
      // or on a websocket ProductUpdated event ever having arrived.
      this.hotReload(true);
    }).catch(err => console.error('Initial product load failed:', err));
    this.checkUrlAndClearStore();
    this.debouncedGetCustomers = this.debounce(this.getCustomers, 400);
    this.getCustomers();
    this.getCompanySettings();

    // PERF: Initialize SKU index for O(1) lookups
    this.buildSkuIndex();

    this.subscribeToProductUpdates();

    // Backgrounded tabs get throttled/frozen by the browser, which can silently
    // drop the WebSocket's keep-alive pings and any ProductUpdated broadcasts
    // that arrive while frozen. Catch up whenever the tab regains visibility
    // instead of relying solely on the live event having been received.
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.hotReload(true);
      }
    };
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeDestroy() {
    this.unsubscribeFromProductUpdates();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },

  data() {
    return {
      // Search & view
      searchItemTerm: '',
      searchCustomer: '',
      isListView: false,
      selectedCategory: '',
      prodCategoryOptions: [],
      s_productcategory: 'All Items',
      sortProducts: null,

      // Products/customers
      fetchedProducts: [],  // hot catalog (≤ 2,000 items)
      allCategories: [],
      allCustomers: [],

      // Catalog metadata — set after load
      isPartialCatalog: false,  // true when total catalog > hot limit
      totalCatalogSize: 0,

      // Server-side search state
      serverSearchResults: [],  // results from /api/pos/search
      serverSearchTotal: 0,
      serverSearchPage: 1,
      serverSearchHasMore: false,
      isServerSearch: false,    // whether we're showing server results vs hot catalog
      serverSearchLoading: false,

      // O(1) SKU → product lookup for barcode scanning
      skuIndex: new Map(),

      // Virtual pagination: only render this many products at a time
      displayLimit: 100,

      // Prevents duplicate in-flight barcode API calls
      barcodeLookupPending: false,

      // Live product-sync channel name, set on mount, used to unsubscribe on destroy
      productUpdatesChannelName: null,

      // Cart & selection
      selecteCartDetails: {},
      newQty: 1,
      newServicePricing: '',
      // Round total modal (separate from the per-unit price modal above -
      // see the comment on roundTotalModal for why)
      selectedRoundTotalCart: null,
      newLineTotal: '',

      // Company & user
      companySettings: {},
      is_Retail: true,

      // Forms & modals
      customerForm: {
        town: '',
        street: '',
        apartment: '',
        h_no: '',
        phone_no: '',
        c_email: '',
        l_name: '',
        f_name: '',
        errorMessage: '',
        krapin: '',
        company: ''
      },

      // Misc
      searchTimeoutId: null,
      customerSearchTimeoutId: null,
      totalDiscountApplied: 0,
      discountError: '',
      message_feedback: '',
      successMessage: '',
      selectedCustomer: '',
      continue_withno_customer: false,
      isSubmitting: false,

      // Local filter cache (hot catalog browsing — search term not cached, Vue handles it)
      cachedFilteredProducts: [],
      lastCategory: '',
      lastSortMode: null,

      // Mobile FAB cart-counter feedback animation
      cartBadgePulse: false,
    };
  },

  computed: {
    // Borrowing only needs the company to have opted in. It's independent of
    // has_stock_buffer: buffer already lets a sale go through on zero stock by
    // silently going negative, but a cashier can still choose to borrow instead
    // (tracked, accounted for) rather than let it go negative untracked.
    canBorrowStock() {
      return Number(this.companySettings.allow_borrowed_stock) !== 0;
    },

    // Display-only mirror of the floor overrideLineTotal (store.js) enforces -
    // keeps the modal's hint text honest without duplicating the real check.
    roundTotalFloor() {
      const cart = this.selectedRoundTotalCart;
      if (!cart) return 0;
      const minPricePerUnit = this.companySettings.price_not_go_below_wholesale
        ? (cart.wholesale_sp || 0)
        : (cart.wholesale_ppu || 0);
      return minPricePerUnit * (parseFloat(cart.quantity) || 0);
    },

    ...mapGetters(['userDetails']),

    canSeeStock() {
      return (this.userDetails && this.userDetails.roles && this.userDetails.roles.length &&
        (this.userDetails.roles?.[0]?.name === 'Admin' ||
         this.companySettings.allow_employees_see_stock_count === true));
    },

    // Filters and sorts the hot catalog (≤ 2,000 items) using the current search
    // term, category, and sort — runs in the Vue reactive system so it re-evaluates
    // instantly on every keystroke with zero network cost.
    // For full catalogs (≤ 2,000 total) this is all you need.
    // For partial catalogs (> 2,000 total), server search supplements these results.
    filteredProducts() {
      const term     = (this.searchItemTerm || '').trim().toLowerCase();
      const category = this.selectedCategory;
      const sort     = this.sortProducts;

      // Cache hit: skip re-filter when nothing that affects the result has changed.
      // Vue's own computed cache already handles the common case; this manual check
      // avoids re-running the filter loop when only unrelated state changes.
      if (!term &&
          category === this.lastCategory &&
          sort      === this.lastSortMode &&
          this.cachedFilteredProducts.length > 0) {
        return this.cachedFilteredProducts;
      }

      const hasStockBuffer = Number(this.companySettings.has_stock_buffer) !== 0;
      const allowBorrow    = this.canBorrowStock;
      const selectedId     = (category && category.id) ? category.id : category;

      let out = this.fetchedProducts.filter(p => {
        if (!p || !p.can_be_sold) return false;
        // A product with variations keeps its stock on each variation (the parent's
        // remaining_units is null, and null <= 0 is true in JS): judge by its variations.
        const stock = p.has_variants
          ? ((p.variants || []).some(v => Number(v.remaining_units) > 0) ? 1 : 0)
          : p.remaining_units;
        const outOfStock = !p.is_service && !hasStockBuffer && stock <= 0;
        // With allow_borrowed_stock on, out-of-stock items stay visible (dimmed,
        // handled by isOutOfStock() in the template) so tapping opens Borrow Stock
        // instead of disappearing from the catalog entirely.
        if (outOfStock && !allowBorrow) return false;
        if (selectedId && selectedId !== '') {
          if (p.productcat_id !== selectedId) return false;
        }
        if (term) {
          const name = (p.product || '').toLowerCase();
          const sku  = String(p.sku  || '').toLowerCase();
          if (!name.includes(term) && !sku.includes(term)) return false;
        }
        return true;
      });

      switch (sort) {
        case 'name-asc':   out.sort((a, b) => a.product.localeCompare(b.product)); break;
        case 'name-desc':  out.sort((a, b) => b.product.localeCompare(a.product)); break;
        case 'price-asc':  out.sort((a, b) => (parseFloat(a.ppu) || 0) - (parseFloat(b.ppu) || 0)); break;
        case 'price-desc': out.sort((a, b) => (parseFloat(b.ppu) || 0) - (parseFloat(a.ppu) || 0)); break;
      }

      // Only cache the no-search state; search terms change every keystroke
      // and Vue's reactive system re-evaluates naturally.
      if (!term) {
        this.lastCategory = category;
        this.lastSortMode = sort;
        this.cachedFilteredProducts = out;
      }

      return out;
    },

    // Priority: server search results (full-catalog) > local filtered hot catalog.
    // Server results only exist for partial catalogs after the debounce fires.
    // Local filtered results are shown immediately while waiting for the server.
    displayedProducts() {
      if (this.isServerSearch && this.serverSearchResults.length > 0) {
        return this.serverSearchResults.slice(0, this.displayLimit);
      }
      return this.filteredProducts.slice(0, this.displayLimit);
    },

    // Total visible count for the header label
    visibleTotal() {
      if (this.isServerSearch) return this.serverSearchTotal;
      return this.filteredProducts.length;
    },

    allCustomersFiltered() {
      const list = Array.isArray(this.allCustomers) ? this.allCustomers : Object.values(this.allCustomers || {});
      const search = (this.searchCustomer || '').trim().toLowerCase();
      if (!search) return list.filter(c => c && c.full_name && c.phone);
      return list.filter(c => {
        if (!c || (!c.full_name && !c.phone)) return false;
        const name = (c.full_name || '').toLowerCase();
        const phone = (c.phone || '').toString().toLowerCase();
        return name.includes(search) || phone.includes(search);
      });
    },

    totalPrice() {
      return this.$store.state.cart.reduce((sum, item) => sum + parseFloat(item.totalPrice || 0), 0);
    },

    grandTotalAmount() {
      return this.totalPrice - (Number(this.totalDiscountApplied) || 0);
    },

    needsCashSession() {
      const settings = this.userDetails?.company?.companysettings;
      return !!(settings?.enable_opening_cash && !this.$store.state.cashSession);
    },
  },

  watch: {
    searchItemTerm(newVal) {
      if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId);

      const term = (newVal || '').trim();

      if (!term) {
        // Cleared: return to hot catalog view (local filteredProducts shows everything)
        this.isServerSearch      = false;
        this.serverSearchResults = [];
        this.serverSearchTotal   = 0;
        this.serverSearchHasMore = false;
        this.displayLimit        = 100;
        return;
      }

      // Local hot-catalog results update INSTANTLY — filteredProducts reacts to
      // searchItemTerm and re-filters the ≤ 2,000 cached products in < 1 ms.

      this.isServerSearch      = false;
      this.serverSearchResults = [];

      const hasLocalResults = this.filteredProducts.length > 0;

      // Full catalog in memory and local results found — no backend call needed.
      if (!this.isPartialCatalog && hasLocalResults) return;

      // Partial catalog, or full catalog with no local match (catches newly-added
      // products not yet in the local cache): fire server search.
      // 0 ms when nothing found locally (show results immediately),
      // 250 ms otherwise (avoid hammering on every mid-word keystroke).
      const delay = hasLocalResults ? 250 : 0;
      this.searchTimeoutId = setTimeout(() => {
        this.runServerSearch(term);
      }, delay);
    },

    searchCustomer() {
      if (this.customerSearchTimeoutId) clearTimeout(this.customerSearchTimeoutId);
      this.customerSearchTimeoutId = setTimeout(() => {
        this.debouncedGetCustomers();
      }, 300);
    },

    grandTotalAmount: {
      handler(newVal) {
        this.$store.commit('UpdateTotalAmount', newVal);
      },
      immediate: true,
    },

    selectedCategory(newVal) {
      // Invalidate cache so filteredProducts re-filters for the new category
      this.cachedFilteredProducts = [];
      this.lastCategory = null;
      this.displayLimit = 100;

      // Switching categories clears text search and returns to hot-catalog view
      this.searchItemTerm      = '';
      this.isServerSearch      = false;
      this.serverSearchResults = [];

      if (newVal && newVal.id) {
        this.s_productcategory = newVal.name || 'Unnamed Category';
      } else if (newVal && typeof newVal === 'string' && newVal !== '') {
        const found = this.prodCategoryOptions.find(opt =>
          (opt.value && opt.value.id === newVal) || opt.value === newVal);
        this.s_productcategory = found ? (found.value.name || 'Unnamed Category') : 'All Items';
      } else {
        this.s_productcategory = 'All Items';
      }
    },

    sortProducts() {
      this.cachedFilteredProducts = [];
      this.displayLimit = 100;
    },

    // Pulses the mobile FAB's counter whenever the cart count goes up, from
    // any entry point (normal add, fast/barcode add, borrowed add) - so
    // there's always visible feedback that something landed in the cart.
    '$store.state.cartCount'(newVal, oldVal) {
      if (typeof oldVal === 'number' && newVal > oldVal) {
        this.pulseCartBadge();
      }
    },
  },

  methods: {
    // Retriggers the CSS animation even if it's already mid-pulse from a rapid
    // second add - toggle off, wait a tick, toggle back on.
    pulseCartBadge() {
      clearTimeout(this._cartPulseTimeout);
      this.cartBadgePulse = false;
      this.$nextTick(() => {
        this.cartBadgePulse = true;
        this._cartPulseTimeout = setTimeout(() => {
          this.cartBadgePulse = false;
        }, 500);
      });
    },

    debounce(fn, wait = 200) {
      let timeout = null;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    // PERF: Build SKU index for instant O(1) lookups
    buildSkuIndex() {
      this.skuIndex.clear();
      this.fetchedProducts.forEach(product => {
        if (product && product.sku) {
          const normalizedSku = String(product.sku).trim().toLowerCase();
          if (normalizedSku) {
            this.skuIndex.set(normalizedSku, product);
          }
        }
      });
    },

    async hotReload(silent = false, bustBackendCache = false) {
      try {
        await productCacheService.hotReloadLocalDB(bustBackendCache, silent);
        await this.displayData('');
        // PERF: Rebuild SKU index after reload
        this.buildSkuIndex();
        this.cachedFilteredProducts = [];

        // displayedProducts renders serverSearchResults instead of
        // filteredProducts whenever isServerSearch is true — refreshing only
        // the local hot catalog above would leave that view stale forever.
        const term = (this.searchItemTerm || '').trim();
        if (this.isServerSearch && term) {
          await this.runServerSearch(term);
        }

        if (!silent) {
          this.$toasts && this.$toasts.push &&
            this.$toasts.push({ type: 'success', message: 'Products refreshed' });
        }
      } catch (err) {
        this.$toasts && this.$toasts.push &&
          this.$toasts.push({ type: 'error', message: 'Failed to refresh products. Please try again.' });
      }
    },

    // Patches one product into fetchedProducts (and serverSearchResults, if
    // that's what's currently displayed) in place, instead of refetching and
    // recaching the entire hot catalog on every single ProductUpdated event.
    // Investigation found the batch /api/pos/catalog/hot response could lag
    // behind a just-committed write in production; a direct single-product
    // lookup avoids that endpoint entirely for the realtime-sync path.
    applyProductUpdate(freshProduct) {
      productCacheService.patchProduct(freshProduct).then(merged => {
        const idx = this.fetchedProducts.findIndex(p => p.id === merged.id);
        if (idx !== -1) {
          this.fetchedProducts.splice(idx, 1, merged);
        } else {
          this.fetchedProducts.push(merged);
        }
        this.cachedFilteredProducts = [];
        this.buildSkuIndex();

        if (this.isServerSearch) {
          const sIdx = this.serverSearchResults.findIndex(p => p.id === merged.id);
          if (sIdx !== -1) {
            this.serverSearchResults.splice(sIdx, 1, merged);
          }
        }
      });
    },

    // Live sync: any terminal that creates/updates/deletes a product broadcasts
    // ProductUpdated, so every other open Sales page for the same company
    // reflects it immediately. Fetches just the changed product directly
    // rather than refetching/recaching the whole hot catalog; falls back to
    // a full hotReload() for deletions (404) or non-Product sources (grouped
    // inventory items don't exist in the products table) or any other error.
    subscribeToProductUpdates() {
      // Guards against a duplicate subscription if mounted() ever runs twice
      // without an intervening beforeDestroy (e.g. HMR in dev).
      if (this.productUpdatesChannelName) return;

      let companyId = null;
      try {
        const userDetails = JSON.parse(window.localStorage.getItem('userDetails'));
        companyId = userDetails && userDetails.company && userDetails.company.id;
      } catch (e) {
        return;
      }
      if (!companyId) return;

      this.productUpdates = createProductUpdateBatcher({
        apply: data => this.applyProductUpdate(data),
        reloadAll: () => this.hotReload(true),
      });
      this.productUpdatesChannelName = `company.${companyId}.products`;
      getEcho().private(this.productUpdatesChannelName)
        .listen('.ProductUpdated', (event) => {
          const productId = event && event.product_id;
          if (!productId) {
            this.productUpdates.reloadSoon();
            return;
          }
          this.productUpdates.add(productId);
        })
        // One event for a whole import: reload the list once.
        .listen('.ProductsChanged', () => this.productUpdates.reloadSoon())
        // Bulk deletes broadcast one batch event with every deleted id,
        // instead of one ProductUpdated per product (see bulkDeleteInventory).
        // The ids are already known to be gone, so drop them from the local
        // hot catalog directly rather than looking each one up (which would
        // just 404) and hotReload()-ing the whole catalog per id.
        .listen('.ProductsDeleted', (event) => {
          this.applyProductsDeleted((event && event.product_ids) || []);
        });
    },

    applyProductsDeleted(productIds) {
      if (!productIds.length) return;
      productCacheService.removeProducts(productIds).then(() => {
        const idSet = new Set(productIds);
        this.fetchedProducts = this.fetchedProducts.filter(p => !idSet.has(p.id));
        this.cachedFilteredProducts = [];
        this.buildSkuIndex();

        if (this.isServerSearch) {
          this.serverSearchResults = this.serverSearchResults.filter(p => !idSet.has(p.id));
        }
      });
    },

    unsubscribeFromProductUpdates() {
      if (window.Echo && this.productUpdatesChannelName) {
        window.Echo.leave(this.productUpdatesChannelName);
        if (this.productUpdates) this.productUpdates.cancel();
      }
      this.productUpdatesChannelName = null;
    },

    showCartModal() {
      if (this.$refs['cart-modal']) this.$refs['cart-modal'].show();
    },

    // Fires a server-side FULLTEXT search and populates serverSearchResults.
    // Rate-limiting is handled by the 250ms debounce in the watcher; we don't
    // guard on serverSearchLoading so a new debounce always replaces stale results.
    runServerSearch(term) {
      if (!term) return;
      this.serverSearchLoading = true;
      this.isServerSearch      = true;
      this.serverSearchPage    = 1;
      this.displayLimit        = 100;

      const catId = this.selectedCategory
        ? (this.selectedCategory.id || this.selectedCategory)
        : '';

      return axios.get('/api/pos/search', { params: { q: term, cat: catId, page: 1 } })
        .then(res => {
          const data = res.data || {};
          this.serverSearchResults = (data.products || []).map(p =>
            Object.isFrozen(p) ? p : Object.freeze(p)
          );
          this.serverSearchTotal   = data.total   || 0;
          this.serverSearchHasMore = data.has_more || false;
        })
        .catch(() => {
          // Server search failed — fall back to local hot catalog search
          this.isServerSearch      = false;
          this.serverSearchResults = [];
        })
        .finally(() => { this.serverSearchLoading = false; });
    },

    // Loads the next page of server search results and appends them.
    loadMoreServerResults() {
      if (!this.serverSearchHasMore || this.serverSearchLoading) return;
      this.serverSearchLoading = true;
      this.serverSearchPage++;

      const catId = this.selectedCategory
        ? (this.selectedCategory.id || this.selectedCategory)
        : '';

      axios.get('/api/pos/search', {
        params: { q: this.searchItemTerm.trim(), cat: catId, page: this.serverSearchPage }
      })
        .then(res => {
          const data     = res.data || {};
          const newItems = (data.products || []).map(p =>
            Object.isFrozen(p) ? p : Object.freeze(p)
          );
          this.serverSearchResults = [...this.serverSearchResults, ...newItems];
          this.serverSearchHasMore = data.has_more || false;
        })
        .catch(() => {})
        .finally(() => { this.serverSearchLoading = false; });
    },

    submitSearchItem() {
      const term = (this.searchItemTerm || '').trim();
      if (!term) return;

      // Cancel any pending server-search debounce — Enter is authoritative.
      if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId);

      const key   = term.toLowerCase();
      const match = this.skuIndex.get(key);
      if (match) {
        // Instant: product was in the hot catalog by exact SKU.
        this.addToCartFast(match);
        this.searchItemTerm = '';
        this.$nextTick(() => {
          if (this.$refs.search_item_focus) this.$refs.search_item_focus.focus();
        });
        return;
      }

      // Enter key / barcode scanner: try exact SKU lookup on the server.
      // This is the ONLY place we call the barcode API — the watcher only
      // does local filtering and debounced full-text search.
      if (!this.barcodeLookupPending) {
        this.barcodeLookupPending = true;
        axios.get('/api/pos/barcode', { params: { sku: term } })
          .then(res => {
            const product = res.data && res.data.product;
            if (product) {
              const frozen = Object.freeze(product);
              this.skuIndex.set(key, frozen);
              this.fetchedProducts.push(frozen);
              this.addToCartFast(frozen);
              this.searchItemTerm = '';
              this.$nextTick(() => {
                if (this.$refs.search_item_focus) this.$refs.search_item_focus.focus();
              });
              return;
            }
            // Not a known barcode — show full-catalog text search results.
            this.runServerSearch(term);
          })
          .catch(() => { this.runServerSearch(term); })
          .finally(() => { this.barcodeLookupPending = false; });
      }
    },

    async getCustomers() {
      try {
        const customers = await customerCacheService.getCustomers(this.searchCustomer || '');
        this.allCustomers = Array.isArray(customers) ? customers :
          (customers ? Object.values(customers) : []);
      } catch (err) {
        // Ignore failure
      }
    },

    isOutOfStock(product) {
      // A has_variants=true parent carries no stock of its own (null,
      // inert - see architecture doc §20/§36 Part 1/8); stock is a
      // per-variant question, resolved once the picker is opened.
      if (!product || product.has_variants) return false;
      const hasStockBuffer = Number(this.companySettings.has_stock_buffer) !== 0;
      return !product.is_service && !hasStockBuffer && product.remaining_units <= 0;
    },

    // remaining_units is a catalog snapshot, not "remaining after the current
    // cart" - so whether there's room for one more unit has to be checked
    // against what's already in the cart (across all lines for this product,
    // borrowed or not - remaining_units already accounts for borrowed-in stock
    // too), not just remaining_units > 0.
    cartQuantityFor(productId) {
      return this.$store.state.cart.reduce(
        (sum, item) => (item.id === productId ? sum + (parseFloat(item.quantity) || 0) : sum),
        0
      );
    },

    // Buffer-independent: is there organically enough stock left for one more
    // unit, ignoring whether the buffer setting would paper over it anyway.
    // This is what decides whether "Borrow instead" is offered at all.
    hasOrganicRoomFor(product) {
      if (!product || product.is_service || product.has_variants) return true;
      return this.cartQuantityFor(product.id) < product.remaining_units;
    },

    // Whether the primary click/scan just succeeds without any special
    // handling - organic room, or the buffer setting bypassing the check
    // entirely (buffer keeps selling into negative silently, as before).
    canAddNormally(product) {
      if (!product) return false;
      const hasStockBuffer = Number(this.companySettings.has_stock_buffer) !== 0;
      return product.is_service || hasStockBuffer || this.hasOrganicRoomFor(product);
    },

    // Independent of has_stock_buffer, unlike isOutOfStock() above - used to offer
    // "Borrow instead" even when the buffer setting would otherwise silently let
    // the sale go through on negative stock. Accounts for what's already in the
    // cart, not just whether the product has any stock at all - e.g. 2 in stock
    // with 2 already in the cart has no room left for a 3rd, same as 0 in stock.
    canBorrowForProduct(product) {
      return this.canBorrowStock && !!product && !product.is_service && !this.hasOrganicRoomFor(product);
    },

    // addToCart() already checks room-in-cart vs stock and routes to the borrow
    // modal itself when there isn't any, so tapping the tile can just delegate.
    handleProductClick(product) {
      this.addToCart(product);
    },

    openBorrowModal(product, prefillQty) {
      if (this.$refs.borrowStockModal) this.$refs.borrowStockModal.open(product, prefillQty);
    },

    // Used by the auto-fallback paths (addToCart/addToCartFast/processNewQty)
    // when stock runs out mid-sale - asks before jumping straight into the
    // Borrow Stock modal, since that's a bigger commitment than just adding
    // to cart. Explicit "Borrow Stock"/"Borrow instead" buttons skip this and
    // call openBorrowModal directly - clicking one of those already is the ask.
    async confirmBorrowStock(product, prefillQty) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${product.product || 'This item'} is out of stock. Would you like to borrow stock to complete the sale?`,
        { title: 'Insufficient Stock', okTitle: 'Borrow Stock', cancelTitle: 'Cancel', okVariant: 'primary', centered: true }
      );
      if (confirmed) this.openBorrowModal(product, prefillQty);
    },

    // Fired by BorrowStockModal once the borrow is recorded server-side - the
    // sale continues on this same screen, cart line tagged so the eventual
    // sale is traceable back to the BorrowedStock row.
    onBorrowed({ borrowedStock, product, quantity }) {
      // Cart price comes from the borrow's agreed selling price, not the
      // product's normal price — that's the whole point of letting it be
      // set (and edited) in the modal.
      const agreedPrice = parseFloat(borrowedStock.selling_price);

      const copy = Object.assign({}, product);
      copy.base_ppu = product.ppu;
      copy.ppu = agreedPrice || product.ppu;
      copy.wholesale_sp = agreedPrice || product.wholesale_sp;
      copy.is_Retail = this.is_Retail;
      copy.companySettings = this.companySettings;
      copy.quantity = quantity;
      copy.borrowed_stock_id = borrowedStock.id;

      this.$store.commit('addBorrowedToCart', copy);
      this.$toasts && this.$toasts.push &&
        this.$toasts.push({ type: 'success', message: 'Borrowed stock added to sale' });
    },

    // Products with has_variants=true carry no sellable stock/price of their
    // own (the parent row is inert once converted - see architecture doc
    // §20/§36 Part 1/8) - a variant must be chosen before anything is added
    // to the cart. Both entry points below check this first.
    openVariantPickerIfNeeded(product) {
      if (!product || !product.has_variants) return false;
      this.$refs.variantPickerModal && this.$refs.variantPickerModal.open(product);
      return true;
    },

    // Fired by VariantPickerModal once the merchant picks an option - the
    // resolved payload is already shaped exactly like a variant barcode scan
    // (PosService::variantToPosArray()), so it goes straight into the normal
    // fast-path cart pipeline unchanged.
    onVariantSelected(variantProduct) {
      this.addToCartFast(variantProduct);
    },

    // PERF: FAST PATH for barcode scanning - bypasses normal addToCart overhead
    addToCartFast(product) {
      if (!product || !product.id) return;
      if (this.openVariantPickerIfNeeded(product)) return;

      // Checks against what's already in the cart, not just remaining_units > 0 -
      // otherwise scanning a product a 3rd time when only 2 are in stock (and 2
      // are already in the cart) silently does nothing (the store mutation's own
      // guard blocks the increment with no feedback at all).
      if (!this.canAddNormally(product)) {
        if (this.canBorrowStock) {
          this.confirmBorrowStock(product, 1);
        } else {
          this.$toasts && this.$toasts.push &&
            this.$toasts.push({ type: 'error', message: 'Insufficient stock' });
        }
        return;
      }

      // PERF: Create isolated cart item (no shared references with fetchedProducts)
      const cartItem = {
        id: product.id,
        product_id: product.product_id || product.id,
        product_variant_id: product.product_variant_id || null,
        product: product.product,
        sku: product.sku,
        ppu: product.effective_price || product.ppu,
        base_ppu: product.ppu,
        wholesale_sp: product.wholesale_sp,
        base_wholesale_sp: product.wholesale_sp,
        remaining_units: product.remaining_units,
        is_service: product.is_service,
        affects_profit: product.affects_profit !== false,
        is_directly_proportional: product.is_directly_proportional,
        is_Retail: this.is_Retail,
        companySettings: this.companySettings,
        // Copy only what's needed for cart display
        img: product.img,
        bg_color: product.bg_color,
        // Unit selector support - see docs/product-variations-units-architecture.md §20
        units: product.units || [],
      };

      // PERF: Use optimized store mutation
      this.$store.commit('addToCartFast', cartItem);
    },

    // Keep original addToCart for click events
    addToCart(product) {
      if (!product || !product.id) return;
      if (this.openVariantPickerIfNeeded(product)) return;

      const copy = Object.assign({}, product);
      copy.product_id = product.product_id || product.id;
      copy.product_variant_id = product.product_variant_id || null;
      copy.base_ppu = product.ppu;
      copy.ppu = product.effective_price || product.ppu;
      copy.base_wholesale_sp = product.wholesale_sp;
      copy.units = product.units || [];
      copy.is_Retail = this.is_Retail;
      copy.companySettings = this.companySettings;

      // Checks against what's already in the cart, not just remaining_units > 0 -
      // e.g. 2 in stock with 2 already in the cart has no room for a 3rd tap,
      // same as if it were fully out of stock.
      if (this.canAddNormally(product)) {
        this.$store.commit('addToCart', copy);
      } else if (this.canBorrowStock) {
        this.confirmBorrowStock(product, 1);
      } else {
        this.$toasts && this.$toasts.push &&
          this.$toasts.push({ type: 'error', message: 'Insufficient stock' });
      }
    },

    removeFromCart(item) {
      this.$store.commit('removeFromCart', item);
    },

    // Fired by the cart line's unit <select> (only rendered when the item has
    // more than one active unit). See store.js setCartUnit for the actual
    // base_quantity/price recomputation.
    changeCartUnit(cart, unitId) {
      const unit = (cart.units || []).find(u => String(u.id) === String(unitId));
      if (!unit) return;
      this.$store.commit('setCartUnit', { cart_id: cart.id, unit });
    },

    showCustomersModal() {
      if (this.$refs['new-customers-modal']) this.$refs['new-customers-modal'].hide();
      if (this.$refs['customers-modal']) this.$refs['customers-modal'].show();
    },

    showCustomersModalFinal() {
      if (this.$refs['default-customers']) this.$refs['default-customers'].hide();
      if (this.$refs['customers-modal-final']) this.$refs['customers-modal-final'].show();
    },

    AddnewcustomerModal() {
      if (this.$refs['customers-modal']) this.$refs['customers-modal'].hide();
      if (this.$refs['new-customers-modal']) this.$refs['new-customers-modal'].show();
      if (this.$refs['cart-modal']) this.$refs['cart-modal'].hide();
    },

    SelectCustomer(customer) {
      this.$store.commit('addOrderingCustomer', customer);
      if (this.$refs['customers-modal']) this.$refs['customers-modal'].toggle();
      this.selectedCustomer = this.$store.state.customerdata.full_name;
      this.message_feedback = '';
    },

    SelectCustomerFinal(customer) {
      this.$store.commit('addOrderingCustomer', customer);
      this.selectedCustomer = this.$store.state.customerdata.full_name;
      this.message_feedback = '';
      this.Submit();
    },

    newCustomerInfo() {
      if (!this.customerForm.phone_no && !this.customerForm.c_email) {
        this.customerForm.errorMessage = 'You need to enter atleast an email or customer phone number';
        return;
      }
      this.customerForm.errorMessage = '';
      axios.post('/api/newcustomer', { ...this.customerForm }).then(result => {
        if (result.status === 200) {
          setTimeout(() => {
            this.successMessage = "<hr/><p><span class='text-success'><i class=\"bi bi-check-circle\"></i>  Customer added successfully</span></p>";
          }, 3000);
          this.$refs['new-customers-modal'] && this.$refs['new-customers-modal'].hide();
          this.$refs['customers-modal'] && this.$refs['customers-modal'].hide();
          this.$store.commit('addOrderingCustomer', result.data.customer);
          this.selectedCustomer = result.data.customer.full_name;
        }
      }).catch(() => {});
    },

    changeQuantity(cart) {
      // Borrowed lines are excluded from cartIndex (see store.js), so
      // UpdateOrderQty can never find them - block the edit here rather than
      // let it silently no-op. The borrowed quantity was fixed at borrow time;
      // remove and re-borrow (or do a separate borrow) to change it.
      if (cart.is_borrowed) {
        this.$toasts && this.$toasts.push &&
          this.$toasts.push({ type: 'info', message: 'Borrowed quantity is fixed — remove and re-borrow to change it.' });
        return;
      }
      this.$refs['orderqty-modal'] && this.$refs['orderqty-modal'].show();
      this.newQty = cart.quantity || 1;
      this.selecteCartDetails = cart;
    },

    changePricingOnTheGo(cart) {
      this.$refs['changePriceModal'] && this.$refs['changePriceModal'].show();
      this.newServicePricing = cart.ppu;
      this.selecteCartDetails = cart;
    },

    checkUrlAndClearStore() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('add-to-order')) {
        this.resetStore();
      }
    },

    resetStore() {
      this.$store.dispatch('resetStore');
    },

    async displayData(selectedCatId) {
      try {
        const [products, categories, meta] = await Promise.all([
          productCacheService.getProducts(),
          productCacheService.getCategories(),
          productCacheService.getMeta(),
        ]);
        this.loadProductData(
          Array.isArray(products)   ? products   : [],
          Array.isArray(categories) ? categories : [],
          selectedCatId,
          meta
        );
      } catch (err) {
        console.error('displayData failed:', err);
        throw err;
      }
    },

    loadProductData(products, categories, selectedCatId, meta) {
      if (!Array.isArray(products))   products   = [];
      if (!Array.isArray(categories)) categories = [];

      // Freeze any product objects that aren't already frozen.
      // Frozen objects are skipped by Vue 2's reactivity walk — eliminates
      // the O(n × fields) getter/setter setup cost on every catalog load.
      const frozen = products.map(p => (Object.isFrozen(p) ? p : Object.freeze({ ...p })));

      if (selectedCatId && selectedCatId !== '') {
        this.fetchedProducts = frozen.filter(p => p.productcat_id === selectedCatId);
      } else {
        this.fetchedProducts = frozen;
      }

      // Store catalog metadata so the template can show accurate counts
      if (meta) {
        this.isPartialCatalog  = meta.is_partial_catalog || false;
        this.totalCatalogSize  = meta.total_catalog_size || frozen.length;
      }

      this.prodCategoryOptions = categories.map(c => ({ value: c, text: c.name }));
      this.allCategories       = categories;
      this.displayLimit        = 100;

      this.buildSkuIndex();
      this.cachedFilteredProducts = [];
    },

    processNewQty() {
      this.$refs['orderqty-modal'] && this.$refs['orderqty-modal'].hide();

      if (!this.selecteCartDetails) return;
      if (!this.selecteCartDetails.is_service) {
        if (this.selecteCartDetails.remaining_units >= this.newQty) {
          const toChangeqQty = {
            quantity: this.newQty,
            cart_id: this.selecteCartDetails.id,
            is_Retail: this.is_Retail
          };
          this.$store.commit('UpdateOrderQty', toChangeqQty);
        } else if (this.canBorrowStock) {
          // Only the shortfall needs borrowing - the rest is already available.
          // (changeQuantity() never opens this modal for an already-borrowed
          // line, so selecteCartDetails here is always a normal cart line.)
          const shortfall = this.newQty - this.selecteCartDetails.remaining_units;
          const product = this.fetchedProducts.find(p => p.id === this.selecteCartDetails.id) || this.selecteCartDetails;
          this.confirmBorrowStock(product, shortfall);
        } else {
          this.$toasts && this.$toasts.push &&
            this.$toasts.push({
              type: 'error',
              message: "The quantity cannot more than " + this.selecteCartDetails.remaining_units
            });
        }
      } else {
        const toChangeqQty = {
          quantity: this.newQty,
          cart_id: this.selecteCartDetails.id,
          is_Retail: this.is_Retail
        };
        this.$store.commit('UpdateOrderQty', toChangeqQty);
      }
    },

    processNewPricesOnTheGo() {
      if (!this.selecteCartDetails) return;
      if (this.selecteCartDetails.is_service ||
          (!this.selecteCartDetails.is_service && this.companySettings.allows_price_change_on_the_go)) {
        this.$refs['changePriceModal'] && this.$refs['changePriceModal'].hide();
        const newServicePricing = {
          newPriceChange: this.newServicePricing,
          cart_id: this.selecteCartDetails.id
        };
        this.$store.commit('updatePricesOnTheGo', newServicePricing);
      }
    },

    // Only for a fractional-qty line on a real stocked item - is_directly_
    // proportional items already have their own "type an amount" flow, and
    // a whole-number qty never has a rounding problem to begin with (the
    // per-unit modal above already handles those cleanly).
    showRoundTotalTrigger(cart) {
      return !cart.is_service && !cart.is_directly_proportional && (parseFloat(cart.quantity) % 1 !== 0);
    },

    openRoundTotalModal(cart) {
      this.selectedRoundTotalCart = cart;
      this.newLineTotal = cart.totalPrice;
      this.$refs['roundTotalModal'] && this.$refs['roundTotalModal'].show();
    },

    saveRoundedTotal() {
      if (!this.selectedRoundTotalCart) return;
      this.$refs['roundTotalModal'] && this.$refs['roundTotalModal'].hide();
      this.$store.commit('overrideLineTotal', {
        cart_id: this.selectedRoundTotalCart.id,
        newTotal: this.newLineTotal,
      });
      this.selectedRoundTotalCart = null;
    },

    formatPrice(value) {
      const val = (value / 1).toFixed(0).replace('.', ',');
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },

    continueWithoutCustomer() {
      this.continue_withno_customer = true;
      this.$refs['default-customers'] && this.$refs['default-customers'].hide();
      this.Submit();
    },

    async Submit() {
      const urlParams = new URLSearchParams(window.location.search);

      if (this.totalDiscountApplied !== "" &&
          !/^\d+(\.\d+)?$/.test(String(this.totalDiscountApplied))) {
        this.discountError = "Discount should be a valid number";
        return;
      } else {
        this.discountError = '';
      }

      if (this.needsCashSession) {
        this.$root.$emit('cash-session-required', {
          retry: () => this.Submit(),
          cancel: () => {},
        });
        return;
      }

      if (this.$store.state.customerdata === 'Select Customer' &&
          this.continue_withno_customer === false) {
        this.$refs['default-customers'] && this.$refs['default-customers'].show();
        return;
      }

      const payload = {
        cart: JSON.stringify(this.$store.state.cart),
        customerdata: JSON.stringify(
          this.$store.state.customerdata === 'Select Customer' ?
            0 : this.$store.state.customerdata
        ),
        totalprice: JSON.stringify(this.$store.state.totalOrderAmount || this.totalPrice),
        discount: this.totalDiscountApplied,
        addTo: urlParams.has('add-to-order') ? urlParams.get('add-to-order') : false
      };

      this.isSubmitting = true;
      try {
        const result = await axios.post('/api/order', payload);
        if (result.status === 200) {
          this.$store.commit('resetStore');
          window.location.href = '/checkout/' + result.data.id;
        }
      } catch (err) {
        this.isSubmitting = false;
      }
    },

    getCompanySettings() {
      if (!this.userDetails) {
        axios.get('/api/settings')
          .then(results => { this.companySettings = results.data || {}; })
          .catch(() => {});
      } else {
        this.companySettings =
          (this.userDetails.company && this.userDetails.company.companysettings) || {};
      }
    },
  },
};
</script>

<style>
.out-of-stock-dim {
  opacity: 0.5;
}
.image-table {
  width: 40px;
  height: 40px;
  object-fit: cover;
  margin-right: 10px;
}
.has-search .form-control {
  padding-left: 2.375rem;
}
.btn-animation {
  animation: fadeIn 0.5s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.cart-count-badge {
  display: inline-block;
}
.cart-count-pulse {
  animation: cartCountPulse 0.5s ease;
}
@keyframes cartCountPulse {
  0% { transform: scale(1); }
  35% { transform: scale(1.6); }
  100% { transform: scale(1); }
}
.has-search .form-control-feedback {
  position: absolute;
  z-index: 2;
  display: block;
  width: 2.375rem;
  height: 2.375rem;
  line-height: 2.375rem;
  text-align: center;
  pointer-events: none;
  color: #aaa;
}
@media only screen and (max-width: 768px) {
  .float {
    position: fixed;
    width: 60px;
    height: 60px;
    bottom: calc(70px + env(safe-area-inset-bottom, 0px));
    right: 40px;
    background-color: #fd4719;
    color: #FFF;
    border-radius: 50px;
    text-align: center;
    border: 3px solid #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,.35);
    z-index: 1040;
  }
  .my-float {
    margin-top: 22px;
    color: #FFF;
  }
}
</style>
